import { Button, Card, CardContent, PageHeader, Skeleton } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../lib/api.js';
import { TRIAL_SOON_DAYS, trialDaysLeft } from '../lib/trial.js';
import { CreditsCard } from './overview/credits-card.js';
import { IntegrationsHealthCard } from './overview/integrations-health-card.js';
import { PlateLookupsCard } from './overview/plate-lookups-card.js';

interface Client {
  id: string;
  email: string;
  createdAt: string;
  archivedAt: string | null;
  lots: Array<{
    id: string;
    subscriptionStatus: string | null;
    trialEndsAt: string | null;
  }>;
}

interface ClientsResponse {
  clients: Client[];
}

const formatDate = (raw: string | null) =>
  raw
    ? new Date(raw).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

const lotCountLabel = (n: number) => (n === 1 ? '1 estacionamento' : `${n} estacionamentos`);

export const OverviewScreen = () => {
  const clients = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.request<ClientsResponse>('/v1/admin/clients'),
  });

  const data = clients.data?.clients ?? [];
  const owners = data.filter((c) => !c.archivedAt).length;
  const lots = data.flatMap((c) => c.lots);
  const onTrial = lots.filter((l) => l.subscriptionStatus === 'trial').length;
  const active = lots.filter((l) => l.subscriptionStatus === 'active').length;
  const pastDue = lots.filter((l) => l.subscriptionStatus === 'past_due').length;
  const trialEndingSoon = lots.filter((l) => {
    const days = trialDaysLeft(l.trialEndsAt);
    return days !== null && days >= 0 && days <= TRIAL_SOON_DAYS;
  }).length;

  return (
    <div className="space-y-6">
      <PageHeader title="Visão geral" description="Saúde geral da operação." />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
        {clients.isLoading ? (
          ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5'].map((id) => (
            <Skeleton key={id} className="h-24 w-full" />
          ))
        ) : (
          <>
            <SummaryCard label="Donos ativos" value={owners.toString()} />
            <SummaryCard label="Estacionamentos" value={lots.length.toString()} />
            <SummaryCard label="Em teste" value={onTrial.toString()} />
            <SummaryCard label="Assinatura ativa" value={active.toString()} tone="success" />
            <SummaryCard label="Atrasados" value={pastDue.toString()} tone="danger" />
          </>
        )}
      </div>

      {trialEndingSoon > 0 && (
        <Card>
          <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm">
              <strong>{trialEndingSoon}</strong> estacionamento(s) com teste grátis terminando em
              até {TRIAL_SOON_DAYS} dias.
            </p>
            <Button asChild variant="outline" size="sm" className="w-full shrink-0 sm:w-auto">
              <Link to="/clients">Ver em Clientes</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <CreditsCard />

      <PlateLookupsCard />

      <IntegrationsHealthCard />

      <Card>
        <CardContent className="pt-6">
          <h2 className="mb-3 text-sm font-semibold">Últimos cadastros</h2>
          {clients.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : clients.isError ? (
            <p className="text-sm text-muted-fg">
              Não foi possível carregar. Tente recarregar a página.
            </p>
          ) : data.length === 0 ? (
            <p className="text-sm text-muted-fg">Nenhum cliente cadastrado ainda.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.slice(0, 10).map((c) => (
                <li
                  key={c.id}
                  className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
                >
                  <span className="min-w-0 truncate text-sm">{c.email}</span>
                  <span className="shrink-0 text-xs text-muted-fg">
                    {lotCountLabel(c.lots.length)} · {formatDate(c.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface SummaryCardProps {
  label: string;
  value: string;
  tone?: 'success' | 'danger';
}

const SummaryCard = ({ label, value, tone }: SummaryCardProps) => (
  <Card>
    <CardContent className="pt-5 sm:pt-6">
      <p className="text-xs uppercase tracking-widest text-muted-fg">{label}</p>
      <p
        className={
          tone === 'success'
            ? 'mt-1 text-2xl font-semibold tabular-nums text-success sm:text-3xl'
            : tone === 'danger'
              ? 'mt-1 text-2xl font-semibold tabular-nums text-danger sm:text-3xl'
              : 'mt-1 text-2xl font-semibold tabular-nums sm:text-3xl'
        }
      >
        {value}
      </p>
    </CardContent>
  </Card>
);
